/**
 * Device secrets (PLAN §9.4): for one studio, each device its config names,
 * whether a secret is set, which options it sets and when — never a value;
 * nothing reads one back. Set replaces a device's secret (option names and
 * values typed into password fields); delete removes it. A secret on a device
 * the config no longer names is listed too, so it can be removed.
 */
import type { DeviceSecretMetaWire } from '@acp/api-contract/deviceSecrets.js';
import { secretKeyWireSchema } from '@acp/api-contract/deviceSecrets.js';
import { type FormEvent, useState } from 'react';
import { Badge } from '../ui/badge.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog.js';
import { Input } from '../ui/input.js';
import { Label } from '../ui/label.js';
import { NativeSelect } from '../ui/native-select.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type DeviceSecretsPanelProps = {
  readonly studios: readonly { readonly id: string; readonly name: string }[];
  readonly studioId: string | null;
  readonly deviceIds: readonly string[];
  readonly secrets: readonly DeviceSecretMetaWire[];
  readonly loading: boolean;
  readonly pending: boolean;
  readonly error: string | null;
  readonly onSelectStudio: (studioId: string) => void;
  readonly onSet: (deviceId: string, values: Readonly<Record<string, string>>) => void;
  readonly onDelete: (deviceId: string) => void;
};

type Row = { id: number; key: string; value: string };

function SetSecretForm({ deviceId, pending, onSubmit, onCancel }: { readonly deviceId: string; readonly pending: boolean; readonly onSubmit: (values: Record<string, string>) => void; readonly onCancel: () => void }) {
  const [rows, setRows] = useState<Row[]>([{ id: 0, key: 'password', value: '' }]);
  const keysOk = rows.every((r) => secretKeyWireSchema.safeParse(r.key.trim()).success) && new Set(rows.map((r) => r.key.trim())).size === rows.length;
  const ready = keysOk && rows.every((r) => r.value !== '');
  const update = (id: number, patch: Partial<Row>) => setRows(rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (ready && !pending) onSubmit(Object.fromEntries(rows.map((r) => [r.key.trim(), r.value])));
  };
  return (
    <form className="grid gap-4" onSubmit={submit}>
      <DialogHeader>
        <DialogTitle>Set the secret of {deviceId}</DialogTitle>
        <DialogDescription>Replaces the device’s secret. Values are sent once and never shown again.</DialogDescription>
      </DialogHeader>
      {rows.map((row) => (
        <div key={row.id} className="grid grid-cols-2 gap-2">
          <div className="grid gap-1">
            <Label htmlFor={`secret-key-${String(row.id)}`}>Option</Label>
            <Input id={`secret-key-${String(row.id)}`} value={row.key} onChange={(e) => update(row.id, { key: e.target.value })} autoComplete="off" data-testid="secret-key" />
          </div>
          <div className="grid gap-1">
            <Label htmlFor={`secret-value-${String(row.id)}`}>Value</Label>
            <Input id={`secret-value-${String(row.id)}`} type="password" value={row.value} onChange={(e) => update(row.id, { value: e.target.value })} autoComplete="new-password" data-testid="secret-value" />
          </div>
        </div>
      ))}
      <Button type="button" variant="ghost" className="w-fit" onClick={() => setRows([...rows, { id: Math.max(...rows.map((r) => r.id)) + 1, key: '', value: '' }])}>
        Add option
      </Button>
      {keysOk ? null : <p className="text-sm text-destructive">Option names are identifiers (a letter, then letters, digits or _), each once.</p>}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Cancel
        </Button>
        <Button type="submit" disabled={!ready || pending} data-testid="save-secret">
          {pending ? 'Saving…' : 'Save secret'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function DeviceSecretsPanel({ studios, studioId, deviceIds, secrets, loading, pending, error, onSelectStudio, onSet, onDelete }: DeviceSecretsPanelProps) {
  const [editing, setEditing] = useState<string | null>(null);
  const byDevice = new Map(secrets.map((s) => [s.deviceId, s]));
  const devices = [...new Set([...deviceIds, ...secrets.map((s) => s.deviceId)])].sort();
  return (
    <Card data-testid="device-secrets">
      <CardHeader>
        <CardTitle>Device secrets</CardTitle>
        <CardDescription>Passwords and tokens a device needs, kept out of the studio config and delivered to the node running the production. They are never shown back.</CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        <div className="grid max-w-sm gap-2">
          <Label htmlFor="secrets-studio">Studio</Label>
          <NativeSelect id="secrets-studio" value={studioId ?? ''} onChange={(e) => onSelectStudio(e.target.value)}>
            {studioId === null ? <option value="">Choose a studio</option> : null}
            {studios.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </NativeSelect>
        </div>
        {error === null ? null : (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        )}
        {studioId !== null && loading ? <p className="text-sm text-muted-foreground">Loading…</p> : null}
        {studioId !== null && !loading && devices.length === 0 ? <p className="text-sm text-muted-foreground">This studio’s config names no devices.</p> : null}
        {studioId !== null && devices.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Device</TableHead>
                <TableHead>Secret</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {devices.map((deviceId) => {
                const secret = byDevice.get(deviceId);
                return (
                  <TableRow key={deviceId} data-testid="secret-row" data-set={secret !== undefined}>
                    <TableCell className="font-mono text-sm">
                      {deviceId}
                      {deviceIds.includes(deviceId) ? null : <span className="ml-2 text-xs text-muted-foreground">(not in the config)</span>}
                    </TableCell>
                    <TableCell className="text-sm">
                      {secret === undefined ? (
                        <Badge variant="outline">not set</Badge>
                      ) : (
                        <>
                          <Badge variant="success">set</Badge>
                          <span className="ml-2 text-xs text-muted-foreground">
                            {secret.keys.join(', ')} · {secret.updatedAt.slice(0, 10)} by {secret.updatedBy}
                          </span>
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" disabled={pending} onClick={() => setEditing(deviceId)} data-testid="set-secret">
                          {secret === undefined ? 'Set' : 'Replace'}
                        </Button>
                        {secret === undefined ? null : (
                          <Button size="sm" variant="ghost" className="text-destructive" disabled={pending} onClick={() => onDelete(deviceId)} data-testid="delete-secret">
                            Delete
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
      <Dialog open={editing !== null} onOpenChange={(open) => (open ? undefined : setEditing(null))}>
        <DialogContent>
          {editing === null ? null : (
            <SetSecretForm
              deviceId={editing}
              pending={pending}
              onCancel={() => setEditing(null)}
              onSubmit={(values) => {
                onSet(editing, values);
                setEditing(null);
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
